import { Checkbox, Tooltip, useDebouncedEffect, useValueRef } from '@timelyapp/react';
import classNames from 'classnames';
import ClearableButton from 'components/buttons/ClearableButton';
import type { OnDropdownChange } from 'components/DropdownBuilder';
import DropdownBuilder from 'components/DropdownBuilder';
import { matchProjectOption } from 'components/ProjectSelect';
import Popover from 'components/popovers/Popover';
import Spinner from 'components/Spinner';
import { EMPTY_ARRAY } from 'constants/defaults';
import type { ProjectDropdownOption } from 'factories/projects';
import { createClientProjectTree, incompleteOption } from 'factories/projects';
import { useOnOff } from 'hooks/useOnOff';
import { isEqual } from 'lodash-es';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import FontAwesome from 'react-fontawesome';
import type { ProjectFetchingStatus } from 'redux/modules/projects.types';
import type { ReportProjectIds } from 'redux/modules/superreports.types';
import type { Client } from 'types/client';
import type { EnrichedProject, FilterGroupValues } from 'types/project';
import type { Tag } from 'types/tag';
import { getProjectFilterLabel } from 'utils/superreports';
import { countableLabel } from 'utils/text-helper';
import { notNull } from 'utils/typesafe';
import { naturalSortBy } from '../../utils/naturalSort';
import FilterDropDownItem from './FilterDropdownItem';
import FilterGroups from './FilterGroups';
import styles from './ProjectFilterDropdown.module.scss';

interface ProjectFilterDropdownProps {
  projects: Array<EnrichedProject>;
  clients: Array<Client>;
  onChange?: (projectIds: ReportProjectIds, filterGroup?: FilterGroupValues) => void;
  onClear?: () => void;
  onSelectProject?: (projectId: number) => void;
  onDeselectProject?: (projectId: number) => void;
  onToggleClient?: (clientId: number, filterByTagIds?: Array<number>) => void;
  onSetFilterGroup?: (group: FilterGroupValues) => void;
  selected: ReportProjectIds;
  /** Override number of selected projects, for when not all selected ids are known but the count by client is known */
  numSelectedByClient?: Record<number, number>;
  /** Override number of selected projects, for when not all selected ids are known but the count is known */
  numSelected?: number;
  /** Override number of total projects, for when not all projects are loaded but the total is still known */
  numTotalProjects?: number;
  numActiveProjects?: number;
  unrestrictedWhenEmpty?: boolean; // defaultProp'd,
  allowSelectNone?: boolean;
  highlightModified?: boolean; // defaultProp'd,
  emptyLabel?: string; // defaultProp'd
  disabled?: boolean;
  onSearch?: (
    filterString: string,
    filterGroup?: FilterGroupValues,
    tagId?: number
  ) => Promise<unknown>;
  /** Shows All bulk action */
  projectFetchingStatus?: ProjectFetchingStatus;
  onSelectAll?: (projectIds: ReportProjectIds) => void;
  fixed?: boolean;
  isLoading?: boolean;

  /** Show project totals on client lines. Only makes sense if user can see all projects */
  showProjectTotalsOnClients?: boolean;
  /** If this props is defined, render "Load more" links in clients when number of projects is less
   * than client.active_projects_count
   */
  onLoadMoreForClient?: (clientId: number) => void;
  onRenderClients?: (clientIds: Array<number>) => void;
  /** If the selection is only partially loaded, pass this flag to indicate this in the component */
  selectionPartiallyLoaded?: boolean;
  size?: 'small' | 'medium';
  tags?: Array<Tag>;
  projectIdsByTagId?: Record<number, Array<number>>;
  /** IGNORE: Added when Storybook started complaining about duplicate id's @ v7 */
  testId?: string;
  /** Project assignees filter mode */
  projectAssigneesFilter?: 'all' | 'project-only';
  onChangeProjectAssigneesFilter?: (mode: 'all' | 'project-only') => void;
  projectAssigneesFilterLimit?: number;
  /** Enable group select all ('all'/'active'/'archived') when not all projects are loaded */
  enableGroupSelectAll?: boolean;
}

interface FilterGroup {
  value: FilterGroupValues;
  name: string;
}

const ProjectFilterDropdown = (props: ProjectFilterDropdownProps) => {
  const {
    allowSelectNone = true,
    clients,
    disabled,
    fixed = false,
    highlightModified,
    isLoading,
    onChange,
    onClear,
    onDeselectProject,
    onLoadMoreForClient,
    onSearch,
    onSelectProject,
    onToggleClient,
    onSetFilterGroup,
    onSelectAll,
    projectFetchingStatus,
    projects,
    selected,
    numSelectedByClient,
    selectionPartiallyLoaded,
    showProjectTotalsOnClients,
    onRenderClients,
    unrestrictedWhenEmpty,
    size,
    tags,
    projectIdsByTagId,
    projectAssigneesFilter = 'all',
    projectAssigneesFilterLimit = 10,
    onChangeProjectAssigneesFilter,
    enableGroupSelectAll = false,
  } = props;

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [setOpen, setClosed] = useOnOff(setIsOpen);
  const [filterGroupSearchState, setFilterGroupInState] = useState<FilterGroupValues>('active');
  const selectedSet = useMemo(() => {
    if (selected === 'all') {
      return new Set(projects.map(project => project.base.id));
    } else if (selected === 'active') {
      return new Set(
        projects.filter(project => project.base.active).map(project => project.base.id)
      );
    } else if (selected === 'archived') {
      return new Set(
        projects.filter(project => !project.base.active).map(project => project.base.id)
      );
    } else {
      return new Set(selected);
    }
  }, [selected, projects]);
  const [selectedTagId, setSelectedTagId] = useState<number>(0);

  const allProjectsLoaded = useMemo(() => {
    if (projectFetchingStatus === 'fetchedAllFull' || projectFetchingStatus === 'fetchedAllLight') {
      return true;
    }
    if (
      projectFetchingStatus === 'fetchedAllActiveFull' ||
      projectFetchingStatus === 'fetchedAllActiveLight'
    ) {
      return filterGroupSearchState === 'active';
    }
    return false;
  }, [projectFetchingStatus, filterGroupSearchState]);

  const setFilterGroup = (g: FilterGroupValues) => {
    setFilterGroupInState(g);
    onSetFilterGroup?.(g);
  };

  const selectedTag = useMemo(() => {
    return tags?.find(tag => tag.id === selectedTagId);
  }, [selectedTagId, tags]);

  const filteredProjects = useMemo(() => {
    return applyGroupFilter(projects, filterGroupSearchState);
  }, [filterGroupSearchState, projects]);

  const [filterString, onSetFilterString] = useState('');
  useDebouncedEffect(
    () => onSearch?.(filterString, filterGroupSearchState, selectedTagId),
    [filterGroupSearchState, filterString, onSearch, selectedTagId],
    300
  );

  const addLoadMoreTriggers = onLoadMoreForClient != null;
  const options: Array<ProjectDropdownOption> = useMemo(
    () =>
      createClientProjectTree(filteredProjects, clients, {
        onlyActive: false,
        addLoadMoreTriggers,
        projectIdsByTagId,
        filterByTagIds: selectedTagId ? [selectedTagId] : undefined,
      }),
    [filteredProjects, clients, addLoadMoreTriggers, projectIdsByTagId, selectedTagId]
  );

  const handleClear = useCallback(
    (e?: React.SyntheticEvent) => {
      e?.stopPropagation();
      onChange?.([]);
      onClear?.();
      setSelectedTagId(0);
      if (projectAssigneesFilter === 'project-only' && onChangeProjectAssigneesFilter) {
        onChangeProjectAssigneesFilter('all');
      }
    },
    [onChange, onClear, projectAssigneesFilter, onChangeProjectAssigneesFilter]
  );

  const numSelected =
    props.numSelected ??
    (Array.isArray(selected)
      ? selected.length
      : selected === 'all'
        ? props.numTotalProjects
        : selected === 'active'
          ? props.numActiveProjects
          : projects.filter(p => !p.base.active).length);
  const emptySelection = numSelected === 0;
  const isEmptyAndUnrestricted = unrestrictedWhenEmpty && emptySelection;
  const showClearButton = unrestrictedWhenEmpty && !emptySelection;

  const isSelected = !!numSelected && !isEmptyAndUnrestricted && highlightModified;

  const hasLoadedArchivedProjects = useMemo(() => projects.some(p => !p.base.active), [projects]);
  const hasArchivedProjects =
    hasLoadedArchivedProjects || props.numActiveProjects !== props.numTotalProjects;
  const filterGroups: null | Array<FilterGroup> = hasArchivedProjects
    ? [
        { value: 'all', name: 'All projects' },
        { value: 'active', name: 'Active' },
        { value: 'archived', name: 'Archived' },
      ]
    : null;

  const numProjects = props.numTotalProjects ?? projects.length;
  let label =
    props.numSelected != null
      ? isEmptyAndUnrestricted
        ? 'Any project'
        : countableLabel('project', {
            selected: numSelected,
            total: allProjectsLoaded ? numProjects : props.numTotalProjects,
          })
      : getProjectFilterLabel(
          selected,
          props.numTotalProjects ?? projects.length,
          props.numActiveProjects ?? projects.filter(p => p.base.active).length
        );
  if (emptySelection && !isEmptyAndUnrestricted && props.emptyLabel) {
    label = props.emptyLabel;
  }

  const clientProjectMap = useMemo(() => {
    const map: {
      [clientId: number]: Array<number>;
    } = {};

    filteredProjects.forEach(({ base: { client, id } }) => {
      if (map[client.id] == null) {
        map[client.id] = [];
      }
      map[client.id].push(id);
    });

    return map;
  }, [filteredProjects]);

  const handleChange: OnDropdownChange<number, ProjectDropdownOption> = useCallback(
    (optionId, option) => {
      if (option == null || optionId == null) {
        return;
      }
      if (option.loadMoreTrigger) {
        if (option.clientId != null && onLoadMoreForClient != null) {
          onLoadMoreForClient(option.clientId);
        }
      } else if (option.project) {
        // Check if we're trying to select a project and we already have maximum selected
        if (
          Array.isArray(selected) &&
          !selectedSet.has(optionId) &&
          selected.length >= projectAssigneesFilterLimit &&
          projectAssigneesFilter === 'project-only'
        ) {
          return;
        }

        if (Array.isArray(selected)) {
          onChange?.(
            selectedSet.has(optionId)
              ? selected.filter(i => i !== optionId)
              : selected.concat([optionId])
          );
          if (selectedSet.has(optionId)) {
            onDeselectProject?.(optionId);
          } else {
            onSelectProject?.(optionId);
          }
        } else {
          onChange?.([optionId]);
        }
      } else if (option.clientId) {
        const projectsForClient = clientProjectMap[option.clientId] || [];
        if (Array.isArray(selected)) {
          if (onChange) {
            const allSelected = projectsForClient.every(id => selectedSet.has(id));
            const filtered = selected.filter(id => !projectsForClient.includes(id));

            if (allSelected) {
              onChange(filtered);
            } else {
              // Check if adding all projects from this client would exceed the limit
              if (projectAssigneesFilter === 'project-only') {
                const newProjects = projectsForClient;
                const totalAfterAdding = filtered.length + projectsForClient.length;

                if (totalAfterAdding > projectAssigneesFilterLimit) {
                  // Only add projects up to the limit
                  const remainingSlots = projectAssigneesFilterLimit - filtered.length;
                  const projectsToAdd = naturalSortBy(
                    newProjects,
                    id => projects.find(p => p.base.id === id)?.base.name ?? String(id)
                  ).slice(0, remainingSlots);
                  const newSelection = filtered.concat(projectsToAdd);
                  if (isEqual(newSelection, selected)) {
                    // Deselect all projects for this client
                    onChange(filtered);
                  } else {
                    onChange(filtered.concat(projectsToAdd));
                  }
                } else {
                  onChange(filtered.concat(projectsForClient));
                }
              } else {
                onChange(filtered.concat(projectsForClient));
              }
            }
          } else if (onToggleClient) {
            onToggleClient(
              option.clientId,
              selectedTagId ? projectIdsByTagId?.[selectedTagId] : undefined
            );
          }
        } else {
          onChange?.(projectsForClient);
        }
      }
    },
    [
      onLoadMoreForClient,
      selectedSet,
      selected,
      projectAssigneesFilterLimit,
      projectAssigneesFilter,
      onChange,
      onDeselectProject,
      onSelectProject,
      onToggleClient,
      clientProjectMap,
      projects,
      selectedTagId,
      projectIdsByTagId,
    ]
  );

  const createHandleBulkAction = (action: 'all' | 'none') => () => {
    if (action === 'none') {
      onChange?.([]);
      onClear?.();
      if (projectAssigneesFilter === 'project-only' && onChangeProjectAssigneesFilter) {
        onChangeProjectAssigneesFilter('all');
      }
    }
    if (action === 'all') {
      if (onSelectAll) {
        onSelectAll(filteredProjects.map(project => project.base.id));
      }
      if (allProjectsLoaded && onChange) {
        // Limit to first 10 projects when selecting all
        const allProjectIds = filteredProjects.map(project => project.base.id);
        if (projectAssigneesFilter === 'project-only') {
          onChange(allProjectIds.slice(0, projectAssigneesFilterLimit));
        } else {
          onChange(allProjectIds);
        }
      } else {
        onChange?.(filterGroupSearchState);
        setIsOpen(false);
      }
    }
  };

  // When we do not load all projects in the workspace (see config.projects.loadLightLimit), we
  // can't show the default "all" action, as it just toggles all loaded project ID's.
  // We can only show a custom "all" action if the parent component provides a callback for it,
  // or if enableGroupSelectAll is explicitly enabled.
  const showSelectAllAction = allProjectsLoaded || onSelectAll != null || enableGroupSelectAll;
  const isAtProjectLimit =
    Array.isArray(selected) &&
    selected.length >= projectAssigneesFilterLimit &&
    projectAssigneesFilter === 'project-only';
  const beyondProjectAssigneeFilterSelectionLimit =
    Array.isArray(selected) && selected.length > projectAssigneesFilterLimit;
  // Only disable "All" button when project assignees filter is active on People tab
  const isAllButtonDisabled =
    projectAssigneesFilter === 'project-only' && onChangeProjectAssigneesFilter != null;

  const showProjectAssigneesFilterBar =
    Array.isArray(selected) && selected.length > 0 && onChangeProjectAssigneesFilter != null;

  const inputActions = (
    <>
      {showSelectAllAction && (
        <button
          type="button"
          onClick={createHandleBulkAction('all')}
          disabled={isAtProjectLimit || isAllButtonDisabled}
          title={
            isAtProjectLimit
              ? `Maximum ${projectAssigneesFilterLimit} projects can be selected when filtering by project assignees`
              : isAllButtonDisabled
                ? `Select ${projectAssigneesFilterLimit} or fewer projects to enable this filter.`
                : undefined
          }
          style={
            isAtProjectLimit || isAllButtonDisabled
              ? { opacity: 0.5, cursor: 'not-allowed' }
              : undefined
          }
        >
          All
        </button>
      )}
      {allowSelectNone && (
        <button type="button" onClick={createHandleBulkAction('none')}>
          None
        </button>
      )}
    </>
  );

  const renderOption = useCallback(
    (item: ProjectDropdownOption) => {
      // Can we have this come for free via DropdownBuilder?
      let checked = false;
      let partiallyChecked;
      let isChild = true;
      const hasCheckbox =
        (item.clientId != null || item.project != null) &&
        !item.createTrigger &&
        !item.loadMoreTrigger;
      let selectedCount: number | undefined;
      let countNotFullyLoaded: boolean | undefined;

      // Check if this individual project should be disabled due to the project limit
      const isProjectDisabledByLimit: boolean = !!(
        item.project &&
        !selectedSet.has(item.value) &&
        isAtProjectLimit
      );

      if (item.children && item.clientId != null) {
        const loadedProjects = clientProjectMap[item.clientId] || [];
        const selectedForClient = numSelectedByClient?.[item.clientId] ?? null;

        selectedCount =
          selectedForClient ??
          loadedProjects.filter(
            // Clamp the selected count by children, so that if children are filtered by tags we
            // still get the correct selected count
            id => selectedSet.has(id) && item.children!.find(project => project.value === id)
          ).length;

        checked = selectedCount > 0;
        countNotFullyLoaded =
          selectionPartiallyLoaded &&
          selectedForClient == null &&
          item.totalProjects !== loadedProjects.length;
        partiallyChecked =
          countNotFullyLoaded || (selectedCount > 0 && selectedCount < item.totalProjects!);
        isChild = false;
      } else {
        checked = Array.isArray(selected) && selected.includes(item.value);
        if (!item.children && item.isSelectable === false) {
          isChild = false;
        }
      }

      return (
        <FilterDropDownItem
          item={item}
          checked={checked}
          disabled={isProjectDisabledByLimit}
          partiallyChecked={partiallyChecked}
          indent={isChild}
          hasCheckbox={hasCheckbox}
          resource={item.children == null ? 'project' : undefined}
          countNotFullyLoaded={countNotFullyLoaded && selectionPartiallyLoaded}
          selectedCount={selectedCount}
          totalCount={showProjectTotalsOnClients ? item.totalProjects : undefined}
          variant={item.loadMoreTrigger ? 'secondary' : undefined}
          nonSelectable={item.isSelectable === false}
        />
      );
    },
    [
      selectedSet,
      isAtProjectLimit,
      selectionPartiallyLoaded,
      showProjectTotalsOnClients,
      clientProjectMap,
      numSelectedByClient,
      selected,
    ]
  );

  const postProcessOptions = useCallback(
    (
      filteredRowNodes: unknown,
      filterString: string,
      pushOption: (normalizedProject: ProjectDropdownOption) => void
    ) => {
      if (!allProjectsLoaded && filterString.length < 1) {
        pushOption(incompleteOption);
      }
    },
    [allProjectsLoaded]
  );

  const handleOptionsRendered = useCallback(
    (renderedOptions: Array<ProjectDropdownOption>) => {
      if (onRenderClients != null) {
        const clientIds = renderedOptions
          .map(option => {
            if (
              !option.createTrigger &&
              !option.loadMoreTrigger &&
              option.project == null &&
              option.clientId != null
            ) {
              return option.clientId;
            }
            return null;
          })
          .filter(notNull);
        if (clientIds.length > 0) {
          onRenderClients(clientIds);
        }
      }
    },
    [onRenderClients]
  );

  const suggestedTags: Array<Tag> = useMemo(() => {
    if (!tags?.length || filterString.length < 3) return EMPTY_ARRAY;

    const rx = new RegExp(filterString, 'i');
    return tags
      .filter(tag => {
        return tag.id !== selectedTagId && rx.test(tag.name);
      })
      .slice(0, 3);
  }, [filterString, selectedTagId, tags]);

  const inputRef = useRef<HTMLInputElement>(null);

  const selectedTagIdRef = useValueRef(selectedTagId);
  const handleClickTag = useCallback(
    (tagId: number) => {
      if (tagId === selectedTagIdRef.current) {
        setSelectedTagId(0);
      } else {
        setSelectedTagId(tagId);
        onSetFilterString('');
      }
      inputRef.current?.focus();
    },
    [selectedTagIdRef]
  );

  return (
    <Popover
      width={352} // Account for border
      isOpen={isOpen}
      onOpen={setOpen}
      onClose={setClosed}
      position="bottom"
      testId={props.testId}
      targetWrapper={false}
      target={
        <ClearableButton
          data-testid={props.testId || 'project-filter-dropdown'}
          priority="medium"
          selected={isSelected}
          disabled={disabled}
          onClear={showClearButton ? handleClear : undefined}
          isActive={null} // Causes PopoverTarget to control caret
          clearTooltip="Clear the project filter"
          className={styles.button}
          size={size}
        >
          <FontAwesome name="briefcase" />
          <span>{label}</span>
          {isLoading && (
            <div
              className={classNames(styles.loadingIndicator, {
                [styles.loadingIndicatorOpen]: isOpen,
                [styles.loadingIndicatorDisabled]: disabled,
              })}
            >
              <div className={styles.spinnerContainer}>
                <Spinner length={6} color="#000" />
              </div>
            </div>
          )}
        </ClearableButton>
      }
    >
      {position => (
        <DropdownBuilder
          value={null}
          // if selected is not an array, we need to pass an empty array to the dropdown
          values={Array.isArray(selected) ? selected : []}
          options={options}
          onChange={handleChange}
          onClear={handleClear}
          isOpen={isOpen}
          onSetIsOpen={setIsOpen}
          renderOption={renderOption}
          optionHeight={28}
          inputIcon={<FontAwesome name="search" />}
          inputCaret={false}
          placeholder="Search projects"
          inputActions={inputActions}
          disabled={disabled}
          filterString={filterString}
          onSetFilterString={onSetFilterString}
          postProcessOptions={postProcessOptions}
          highlightSelection={false}
          popoverFixed={fixed}
          matchOption={matchProjectOption}
          onOptionsRendered={handleOptionsRendered}
          inputRef={inputRef}
        >
          {context => (
            <div>
              {filterGroups && (
                <div style={{ padding: '12px 12px 0 12px' }}>
                  <FilterGroups<FilterGroup['value'], FilterGroup>
                    active={filterGroupSearchState}
                    groups={filterGroups}
                    onChange={setFilterGroup}
                  />
                </div>
              )}
              <div style={{ padding: filterGroups ? '6px 12px 12px' : 12 }}>
                {context.renderInput({ autoFocus: true, disabled })}
              </div>
              {!!(suggestedTags.length || selectedTag) && (
                <div style={{ padding: '0 12px' }}>
                  {context.renderTags({
                    suggestedTags,
                    selectedTag,
                    onClickTag: handleClickTag,
                  })}
                </div>
              )}
              {isAtProjectLimit && (
                <div
                  style={{
                    padding: '8px 12px',
                    fontSize: '12px',
                    lineHeight: '1.3',
                    color: '#6b7280',
                    backgroundColor: '#f9fafb',
                    borderTop: '1px solid #e5e7eb',
                    borderBottom: '1px solid #e5e7eb',
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: 8,
                  }}
                >
                  <FontAwesome name="info-circle" />
                  <div>
                    Maximum {projectAssigneesFilterLimit} projects can be selected when filtering by
                    project assignees. Deselect projects to add new ones.
                  </div>
                </div>
              )}
              {context.renderList({
                position,
              })}
              {showProjectAssigneesFilterBar && (
                <div className={styles.projectAssigneesFilterContainer}>
                  <div className={styles.projectAssigneesFilter}>
                    <Tooltip
                      tooltip={
                        beyondProjectAssigneeFilterSelectionLimit &&
                        projectAssigneesFilter !== 'project-only' ? (
                          <div>
                            Select {projectAssigneesFilterLimit} or fewer projects to enable this
                            filter.
                          </div>
                        ) : undefined
                      }
                      position="top"
                      usePortal={true}
                    >
                      <Checkbox
                        label="Show only project assignees"
                        checked={projectAssigneesFilter === 'project-only'}
                        onChangeChecked={checked =>
                          onChangeProjectAssigneesFilter(checked ? 'project-only' : 'all')
                        }
                        className={classNames(styles.option, {
                          [styles.disabled]:
                            beyondProjectAssigneeFilterSelectionLimit &&
                            projectAssigneesFilter !== 'project-only',
                        })}
                        data-testid="project-assignees-filter-checkbox"
                        disabled={
                          beyondProjectAssigneeFilterSelectionLimit &&
                          projectAssigneesFilter !== 'project-only'
                        }
                        aria-disabled={
                          beyondProjectAssigneeFilterSelectionLimit &&
                          projectAssigneesFilter !== 'project-only'
                        }
                      />
                    </Tooltip>
                  </div>
                </div>
              )}
            </div>
          )}
        </DropdownBuilder>
      )}
    </Popover>
  );
};

ProjectFilterDropdown.defaultProps = {
  emptyLabel: 'Projects',
  unrestrictedWhenEmpty: false,
  highlightModified: true,
};

export default ProjectFilterDropdown;

const applyGroupFilter = (
  projects: Array<EnrichedProject>,
  filterGroup: FilterGroupValues
): Array<EnrichedProject> => {
  if (filterGroup === 'all') {
    return projects;
  }
  return projects.filter(p => p.base.active === (filterGroup === 'active'));
};
